import { ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useScrollHint } from '../hooks/useScrollHint'
import { addonCatalogSupports, getAddonCatalogPages, getAddonManifest, getCatalogPages, type AddonCatalogDescriptor } from '../lib/stremio'
import { coversYears, titleYears } from '../lib/editorial'
import type { AddonConfig, MediaType, MetaPreview } from '../types'
import { MediaCard } from './MediaCard'
import { SkeletonRow } from './SkeletonRow'

const GENRES = [
  'All genres', 'Action', 'Adventure', 'Animation', 'Comedy', 'Crime', 'Documentary',
  'Drama', 'Family', 'Fantasy', 'History', 'Horror', 'Mystery', 'Romance', 'Sci-Fi',
  'Thriller', 'War', 'Western',
]
const CURRENT_YEAR = new Date().getFullYear()
const YEARS = ['All years', ...Array.from({ length: 7 }, (_, index) => String(CURRENT_YEAR - index)), '2010s', 'Classics']
const PAGE_SIZE = 18

type Sort = 'popular' | 'rating' | 'newest'

interface Props {
  type: MediaType
  onSelect: (item: MetaPreview) => void
  addons?: AddonConfig[]
}

interface AvailableCatalog {
  key: string
  addon: AddonConfig
  catalog: AddonCatalogDescriptor
}

export function BrowseScreen({ type, onSelect, addons = [] }: Props) {
  const [genre, setGenre] = useState('All genres')
  const [year, setYear] = useState('All years')
  const [sort, setSort] = useState<Sort>('popular')
  const [items, setItems] = useState<MetaPreview[]>([])
  const [loading, setLoading] = useState(true)
  // fade the trailing edge only on facet rows that genuinely overflow
  useScrollHint('.browse-screen .filter-scroll', [type])
  const [error, setError] = useState('')
  const [attempt, setAttempt] = useState(0)
  const [page, setPage] = useState(0)
  const [addonCatalogs, setAddonCatalogs] = useState<AvailableCatalog[]>([])
  const [catalogSource, setCatalogSource] = useState('cinemeta')

  useEffect(() => {
    setCatalogSource('cinemeta')
    setAddonCatalogs([])
    const providers = addons.filter((addon) => addon.enabled && (!addon.resources || addon.resources.includes('catalog')))
    if (!providers.length) return
    let active = true
    Promise.allSettled(providers.map(async (addon) => ({ addon, manifest: await getAddonManifest(addon.url) })))
      .then((results) => active && setAddonCatalogs(results.flatMap((result) => {
        if (result.status !== 'fulfilled') return []
        return (result.value.manifest.catalogs || [])
          .filter((catalog) => catalog.type === type && catalog.id)
          .map((catalog) => ({ key: `${result.value.addon.id}:${catalog.id}`, addon: result.value.addon, catalog }))
      })))
    return () => { active = false }
  }, [addons, type])

  useEffect(() => {
    let active = true
    setLoading(true)
    setError('')
    setPage(0)

    const exactYear = /^\d{4}$/.test(year) ? year : undefined
    const broadRange = year === '2010s' || year === 'Classics'
    // Cinemeta's year catalog requires one exact year. Do not send “2010s”
    // or silently substitute the current year: that previously made these
    // two visible filters return an empty page. Broad ranges use a deeper
    // real catalog sample, then apply their range locally.
    const useYearCatalog = Boolean(exactYear) || (sort === 'newest' && !broadRange)
    const catalog = useYearCatalog ? 'year' : sort === 'rating' ? 'imdbRating' : 'top'
    const catalogGenre = useYearCatalog ? exactYear || String(CURRENT_YEAR) : genre === 'All genres' ? undefined : genre

    const selectedAddonCatalog = addonCatalogs.find((entry) => entry.key === catalogSource)
    const addonCatalog = selectedAddonCatalog?.catalog
    const request = addonCatalog && selectedAddonCatalog
      ? getAddonCatalogPages(selectedAddonCatalog.addon.url, addonCatalog, {
        genre: genre !== 'All genres' && addonCatalogSupports(addonCatalog, 'genre') ? genre : undefined,
      }, broadRange ? 5 : 3)
      : getCatalogPages(type, catalog, { genre: catalogGenre }, broadRange ? 5 : 3)

    request
      .then((value) => {
        if (!active) return
        const filtered = value.filter((item) => {
          const itemGenres = item.genre || item.genres || []
          if ((useYearCatalog || (addonCatalog && !addonCatalogSupports(addonCatalog, 'genre'))) && genre !== 'All genres' && itemGenres.indexOf(genre) < 0) return false
          // A series that ran 2019-2023 belongs under every year it covers, not
          // only the one it started in.
          if (exactYear && !coversYears(item, Number(exactYear), Number(exactYear))) return false
          if (year === '2010s' && !coversYears(item, 2010, 2019)) return false
          if (year === 'Classics') {
            const start = titleYears(item).start
            if (!start || start >= 2010) return false
          }
          return true
        })
        filtered.sort((a, b) => sort === 'rating'
          ? (Number(b.imdbRating) || 0) - (Number(a.imdbRating) || 0)
          : sort === 'newest'
            ? titleYears(b).start - titleYears(a).start
            : 0)
        setItems(filtered)
      })
      .catch((reason) => {
        if (!active) return
        // Results from the previous filter must not sit under the error message
        // claiming to be this one's — the grid would say 2023 while showing
        // whatever the last successful request returned.
        setItems([])
        setError(reason instanceof Error ? reason.message : 'Could not load this catalog.')
      })
      .finally(() => active && setLoading(false))
    return () => { active = false }
  }, [addonCatalogs, attempt, catalogSource, genre, sort, type, year])

  const pages = Math.max(1, Math.ceil(items.length / PAGE_SIZE))
  const visible = useMemo(() => items.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE), [items, page])
  const title = type === 'movie' ? 'Movies' : 'Series'

  return (
    <div className="screen browse-screen">
      <header className="discover-header">
        <div className="screen-title">
          <h1>{title}</h1>
          <p>Browse live Cinemeta and catalogs declared by your configured add-on.</p>
        </div>
        <div className="filter-tabs" data-nav-group="row" data-nav-key={`${type}:sort`}>
          {(['popular', 'rating', 'newest'] as Sort[]).map((value) => (
            <button
              className={`filter-button${sort === value ? ' active' : ''}`}
              data-focusable
              data-autofocus={value === 'popular' ? '' : undefined}
              data-screen-entry={value === 'popular' ? (type === 'movie' ? 'movies' : 'series') : undefined}
              data-nav-up-section={type === 'movie' ? 'movies' : 'series'}
              onClick={() => setSort(value)}
              key={value}
            >{value === 'popular' ? 'Popular' : value === 'rating' ? 'Top rated' : 'Newest'}</button>
          ))}
        </div>
      </header>

      {addonCatalogs.length ? (
        <div className="browse-filter-block" data-scroll-hint>
          <span>Catalog</span>
          <div className="filter-tabs filter-scroll" data-nav-group="row" data-nav-key={`${type}:catalog-source`}>
            <button className={`filter-button${catalogSource === 'cinemeta' ? ' active' : ''}`} data-focusable onClick={() => setCatalogSource('cinemeta')}>Cinemeta</button>
            {addonCatalogs.map((entry) => (
              <button className={`filter-button${catalogSource === entry.key ? ' active' : ''}`} data-focusable onClick={() => setCatalogSource(entry.key)} key={entry.key}>{entry.catalog.name || entry.catalog.id} · {entry.addon.name}</button>
            ))}
          </div>
        </div>
      ) : null}

      <div className="browse-filter-block" data-scroll-hint>
        <span>Genre</span>
        <div className="filter-tabs filter-scroll" data-nav-group="row" data-nav-key={`${type}:genre`}>
          {GENRES.map((value) => (
            <button className={`filter-button${genre === value ? ' active' : ''}`} data-focusable onClick={() => setGenre(value)} key={value}>{value}</button>
          ))}
        </div>
      </div>
      <div className="browse-filter-block" data-scroll-hint>
        <span>Year</span>
        <div className="filter-tabs filter-scroll" data-nav-group="row" data-nav-key={`${type}:year`}>
          {YEARS.map((value) => (
            <button className={`filter-button${year === value ? ' active' : ''}`} data-focusable onClick={() => setYear(value)} key={value}>{value}</button>
          ))}
        </div>
      </div>

      {loading ? <><SkeletonRow /><SkeletonRow /></> : null}
      {error ? (
        <div className="catalog-error">
          <span><strong>Catalog unavailable</strong>{error}</span>
          <div className="retry-actions"><button className="secondary-button" data-focusable data-autofocus onClick={() => setAttempt((value) => value + 1)}><RefreshCw size="1.5rem" /> Try again</button></div>
        </div>
      ) : null}
      {!loading && !error && !visible.length ? <div className="search-empty"><h2>No titles here</h2><p>Try a broader genre or year.</p></div> : null}
      {visible.length ? (
        <>
          <div className="media-grid" data-nav-group="grid" data-nav-columns="6" data-nav-key={`${type}:catalog:${page}`}>
            {visible.map((item, index) => <MediaCard key={`${item.type}:${item.id}`} item={item} onSelect={onSelect} autofocus={index === 0} />)}
          </div>
          {pages > 1 ? <div className="catalog-pager" data-nav-group="row" data-nav-key={`${type}:pages`}>
            <button className="secondary-button" data-focusable disabled={page === 0} onClick={() => setPage((value) => Math.max(0, value - 1))}><ChevronLeft size="1.5rem" /> Previous</button>
            <span>Page {page + 1} of {pages} · {items.length} titles loaded</span>
            <button className="secondary-button icon-after" data-focusable disabled={page >= pages - 1} onClick={() => setPage((value) => Math.min(pages - 1, value + 1))}>Next <ChevronRight size="1.5rem" /></button>
          </div> : null}
        </>
      ) : null}
    </div>
  )
}
